import React, {useRef} from 'react';
import useGetSyncPromise from '../../utils/react/useGetSyncPromise';
import fetchUser from '../../utils/gaia/fetchUser';
import {metersToUsersUnits, metersToUsersDistanceUnits} from '../../utils/formatters';
import LineChart from './LineChart';
import {flatten, map, every} from 'lodash';
import getTrackLineRequests from '../../utils/gaia/getTrackLineRequests';
import turfDistance from '@turf/distance';
import turfLength from '@turf/length';
import turfLineSlice from '@turf/line-slice';
import turfAlong from '@turf/along';
import convertLngLat from '../../utils/mapbox/convertLngLat';
import fetchElevation from '../../utils/gaia/fetchElevation';

interface ElevationProfileProps extends React.SVGProps<SVGSVGElement> {
    track: Track;
    interaction: MapInteraction;
    project: MapProject;
    width: number;
    height: number;
    onHighlightLocationChange: SetState<mapboxgl.LngLat | undefined>;
    hoverLngLat?: mapboxgl.LngLat;
    dataColor?: string;
}

export default function ElevationProfile({
    track,
    interaction,
    project,
    width,
    height,
    onHighlightLocationChange,
    ...props
}: ElevationProfileProps) {

    const getSyncPromise = useGetSyncPromise();

    const [user] = getSyncPromise(fetchUser());

    const coordinatesRef = useRef<number[][]>([]);
    let coordinates = coordinatesRef.current;

    if (interaction.subtype !== 'draggingPoint') {
        const geometries = getTrackLineRequests({track, project, interaction}).map(request => {
            if (request.type === 'async') {
                const [geometry] = getSyncPromise(request.geometry);
                if (!geometry) return request.loadingGeometry;
                return getSyncPromise(fetchElevation(geometry))[0];
            } else {
                return getSyncPromise(fetchElevation(request.geometry))[0];
            }
        });

        if (every(geometries)) {
            coordinates = flatten(map(geometries, geometry => geometry!.coordinates));
        }
    }

    coordinatesRef.current = coordinates;

    let distance = 0;
    const data: [number, number | undefined][] = [];
    for (let i = 0; i < coordinates.length - 1; i++) {
        const elevation: number | undefined = coordinates[i][2];
        data.push([
            metersToUsersDistanceUnits(distance, user),
            elevation === undefined ? undefined : metersToUsersUnits(elevation, user)
        ]);
        if (i < coordinates.length - 1) {
            distance += turfDistance(coordinates[i], coordinates[i + 1], {units: 'meters'});
        }
    }

    const hoverX = (
        (interaction.subtype === 'hoveringLine' || interaction.subtype === 'hoveringPoint') &&
        interaction.trackLocalId === track.properties.trackLocalId &&
        coordinates.length > 1
    ) ?
        metersToUsersDistanceUnits(turfLength(
            turfLineSlice(
                coordinates[0],
                interaction.lngLat.toArray(),
                {type: 'LineString', coordinates}
            ),
            {units: 'meters'}
        ), user) :
        undefined;

    return <LineChart
        {...props}
        xUnits={user && user.unitSystem === 'metric' ? 'km' : 'mi'}
        yUnits={user && user.unitSystem === 'metric' ? 'm' : 'ft'}
        width={width}
        height={height}
        data={data}
        hoverX={hoverX}
        initialYMax={100}
        onLineChartMouseMove={event => {
            if (!onHighlightLocationChange) return;
            if (!event) return onHighlightLocationChange(undefined);
            if (!coordinates.length) return onHighlightLocationChange(undefined);

            onHighlightLocationChange(convertLngLat(turfAlong(
                {type: 'Feature', properties: {}, geometry: {coordinates, type: 'LineString'}},
                event.dataX,
                {units: user && user.unitSystem === 'metric' ? 'kilometers' : 'miles'}
            ).geometry.coordinates));
        }}
        onMouseLeave={() => onHighlightLocationChange(undefined)}
    />;
}
